import { C, L, Note, P, Terms } from '../prose';
import type { Topic } from '../types';

/** The editor: what the banner says, how it looks, and how a change reaches visitors. */
export const banner = {
  'banner-inhalt': {
    group: 'banner',
    title: { de: 'Titel, Text, Links und Logo', en: 'Title, text, links and logo' },
    teaser: {
      de: 'Der Wortlaut, der später im Nachweis steht.',
      en: 'The wording that later appears in the proof.',
    },
    body: {
      de: (
        <>
          <P>
            Titel und Text sind das, was der Besucher gelesen hat, als er entschieden hat. Genau
            dieser Wortlaut wird mit jeder Entscheidung festgehalten — Art. 7 (1) DSGVO verlangt
            nicht den Nachweis eines Klicks, sondern den Nachweis, worauf sich der Klick bezog.
          </P>
          <P>
            Sagen Sie, was passiert und wer es bekommt. „Wir verwenden Cookies, um Ihr Erlebnis
            zu verbessern“ benennt weder Zweck noch Empfänger und ist der Satz, der in
            Beanstandungen am häufigsten zitiert wird.
          </P>
          <Terms
            items={[
              ['Rechtliche Links', 'Datenschutzerklärung und Impressum. Sie müssen aus dem Banner erreichbar sein, ohne es vorher wegzuklicken.'],
              ['Logo', 'Rein dekorativ? Alternativtext leer lassen — sonst liest ihn ein Screenreader vor jeder Entscheidung vor.'],
            ]}
          />
        </>
      ),
      en: (
        <>
          <P>
            The title and body are what the visitor read when they decided. That exact wording is
            recorded with every decision — Art. 7 (1) GDPR does not ask you to prove a click, it
            asks you to prove what the click referred to.
          </P>
          <P>
            Say what happens and who receives it. "We use cookies to improve your experience"
            names neither a purpose nor a recipient, and is the sentence most often quoted back
            in complaints.
          </P>
          <Terms
            items={[
              ['Legal links', 'Privacy policy and imprint. They must be reachable from the banner without dismissing it first.'],
              ['Logo', 'Purely decorative? Leave the alt text empty — otherwise a screen reader announces it before every decision.'],
            ]}
          />
        </>
      ),
    },
  },

  kategorien: {
    group: 'banner',
    title: { de: 'Kategorien', en: 'Categories' },
    teaser: {
      de: 'Wonach getrennt eingewilligt wird — und warum nichts vorausgewählt sein darf.',
      en: 'What consent is split into — and why nothing may be pre-ticked.',
    },
    body: {
      de: (
        <>
          <P>
            Eine Einwilligung muss granular sein: der Besucher darf Statistik erlauben und
            Marketing ablehnen. Die Reihenfolge in der Liste ist die Reihenfolge im Dialog und
            wird gezogen, nicht als Zahl eingetippt.
          </P>
          <Terms
            items={[
              ['Notwendig', 'Immer aktiv, nicht abwählbar. Was hier hineinsortiert wird, läuft ohne Einwilligung — die Kategorie ist keine Abkürzung für „möchten wir immer laden“.'],
              ['Vorausgewählt', 'Unter „Einwilligung vorab“ ist eine vorausgewählte optionale Kategorie keine freiwillige Einwilligung. Der Editor warnt davor.'],
              ['Eigene Kategorien', 'Kleinbuchstaben, Ziffern, Unterstrich. Eine Kategorie, die noch ein Anbieter benutzt, lässt sich nicht löschen.'],
            ]}
          />
          <Note>
            Jede Änderung an einer Bezeichnung oder an der Vorauswahl ist eine inhaltliche
            Änderung: sie ändert den Nachweis-Hash, und alle bestehenden Einwilligungen werden
            erneut abgefragt.
          </Note>
        </>
      ),
      en: (
        <>
          <P>
            Consent has to be granular: a visitor may allow analytics and refuse marketing. The
            order in this list is the order in the dialog, and it is dragged rather than typed as
            a number.
          </P>
          <Terms
            items={[
              ['Necessary', 'Always on, never unticked. Whatever you sort in here runs without consent — the category is not shorthand for "we would always like to load this".'],
              ['Pre-selected', 'Under "consent first", a pre-ticked optional category is not freely given consent. The editor warns about it.'],
              ['Custom categories', 'Lowercase, digits, underscore. A category still used by a vendor cannot be deleted.'],
            ]}
          />
          <Note>
            Any change to a label or to the pre-selection is a material change: it alters the
            proof hash, and every existing consent is asked for again.
          </Note>
        </>
      ),
    },
  },

  darstellung: {
    group: 'banner',
    title: { de: 'Layout, Farben und Schrift', en: 'Layout, colours and type' },
    teaser: {
      de: 'Was frei wählbar ist und wo der Editor eine Grenze zieht.',
      en: 'What is free to choose, and where the editor draws a line.',
    },
    body: {
      de: (
        <>
          <P>
            Layout, Größe, Abstände, Farben und Schriftgröße sind Ihre Entscheidung. Zwei Dinge
            sind es nicht: der Kontrast und die Herkunft der Schrift.
          </P>
          <P>
            Der Kontrast zwischen Fläche und Schrift muss mindestens 4,5:1 betragen (WCAG 2.2
            AA). Der Wert steht neben dem Farbwähler und bewegt sich, während Sie ihn bewegen —
            unter der Grenze lässt sich nicht veröffentlichen.
          </P>
          <P>
            Eigene Schriften darf das Banner laden — aber nur von Ihrer eigenen Website.
            Überschrift und Fließtext lassen sich getrennt festlegen, jeweils mit Familienname
            und Adresse der Schriftdatei. Von Ihrer Seite geladen ist es dieselbe Herkunft wie
            die Seite selbst; von Google Fonts oder einem fremden CDN wäre es eine Anfrage an
            einen Dritten, bevor eingewilligt wurde — also genau das, was das Produkt verhindert.
          </P>
          <P>
            Geben Sie die Adresse am besten als Pfad ohne Domain an, etwa
            <C>/fonts/brand-sans.woff2</C>. Eine Website antwortet meist unter mehreren
            Adressen — mit und ohne <C>www</C>, dazu die Testumgebung —, und nur ein Pfad
            ist auf allen dieselbe Herkunft. Eine vollständige Adresse gilt für genau einen Host;
            auf allen anderen wäre sie ein Aufruf bei einem Dritten. Der Editor weist darauf hin,
            wenn der Host nicht zu den Domains dieser Website gehört, verbietet es aber nicht:
            welche Hosts Ihre Dateien ausliefern, wissen Sie besser als die Konsole.
          </P>
          <P>
            Die Ausweichschrift bleibt Pflicht. Sie zeichnet den Text, solange die Schriftdatei
            noch unterwegs ist, und dauerhaft auf jedem Gerät, auf dem sie nie ankommt.
          </P>
          <Note>
            <strong>Anpassen</strong> zeigt die Komposition, <strong>100 %</strong> zeigt die
            Wahrheit. Bei eingepasster Vorschau wird der Bannertext mit etwa 8 px gezeichnet —
            Wortlaut und Kontrast lassen sich daran nicht beurteilen.
          </Note>
        </>
      ),
      en: (
        <>
          <P>
            Layout, size, spacing, colours and type size are yours to choose. Two things are not:
            contrast and where a typeface comes from.
          </P>
          <P>
            Contrast between surface and text must be at least 4.5:1 (WCAG 2.2 AA). The figure
            sits next to the colour picker and moves while you move it — below the threshold,
            publishing is refused.
          </P>
          <P>
            The banner may load your own fonts — but only from your own website. Headline and
            body face are configured separately, each with a family name and the address of the
            font file. Served by your site it is the same origin as the page itself; from Google
            Fonts or a foreign CDN it would be a request to a third party before consent, which
            is the exact thing this product prevents.
          </P>
          <P>
            Give the address as a path without a domain where you can, e.g.
            <C>/fonts/brand-sans.woff2</C>. A site usually answers on several addresses —
            with and without <C>www</C>, plus staging — and only a path is same-origin on
            all of them. A full URL holds for exactly one host; everywhere else it is a
            third-party call. The editor warns when the host is not one of this site's domains,
            but does not forbid it: you know which hosts serve your files better than the console
            does.
          </P>
          <P>
            The fallback stack stays mandatory. It draws the text while the file is still in
            flight, and permanently on any device where it never arrives.
          </P>
          <Note>
            <strong>Fit</strong> shows the composition, <strong>100 %</strong> shows the truth. At
            fit scale the banner's own text is drawn at about 8 px — you cannot judge wording or
            contrast from it.
          </Note>
        </>
      ),
    },
  },

  buttons: {
    group: 'banner',
    title: { de: 'Buttons und Dark Patterns', en: 'Buttons and dark patterns' },
    teaser: {
      de: 'Reihenfolge, Gewichtung, und die Anordnungen, die beanstandet werden.',
      en: 'Order, weight, and the arrangements that get challenged.',
    },
    body: {
      de: (
        <>
          <P>
            Die Reihenfolge im HTML ist die Vorlese- und Tab-Reihenfolge (WCAG 2.4.3). Sie
            entscheidet, was ein Screenreader zuerst nennt und was die Tastatur zuerst erreicht —
            sie ist damit eine Compliance-Fläche und keine Dekoration.
          </P>
          <L
            items={[
              <>Ablehnen als Link neben Akzeptieren als Button ist der klassische Dark-Pattern-Vorwurf.</>,
              <>Akzeptieren vor Ablehnen ist erlaubt und die Anordnung, die am ehesten beanstandet wird.</>,
              <>Ohne „Einstellungen“ gibt es keinen Weg zur granularen Auswahl — und ohne den ist die Einwilligung nicht granular.</>,
            ]}
          />
          <P>
            <strong>Gleiche Gewichtung</strong> gibt Ablehnen und Akzeptieren dieselbe Fläche und
            dieselben Farben. Wo das Regime einen sichtbaren Ablehnen-Button verlangt, prüft der
            Server das beim Veröffentlichen — der Editor kann es nicht umgehen.
          </P>
        </>
      ),
      en: (
        <>
          <P>
            Order in the HTML is reading order and tab order (WCAG 2.4.3). It decides what a
            screen reader announces first and what the keyboard reaches first — which makes it a
            compliance surface, not decoration.
          </P>
          <L
            items={[
              <>Reject as a link beside accept as a button is the classic dark-pattern accusation.</>,
              <>Accept before reject is permitted, and is the arrangement most likely to be challenged.</>,
              <>Without "Settings" there is no route to the granular choice — and without that, consent is not granular.</>,
            ]}
          />
          <P>
            <strong>Equal weight</strong> gives reject and accept the same area and the same
            colours. Where the regime requires a visible reject button, the server checks it at
            publish time — the editor cannot route around it.
          </P>
        </>
      ),
    },
  },

  widerruf: {
    group: 'banner',
    title: { de: 'Widerruf-Badge', en: 'Withdrawal badge' },
    teaser: {
      de: 'Warum es einen dauerhaften Weg zurück geben muss.',
      en: 'Why there has to be a permanent way back.',
    },
    body: {
      de: (
        <>
          <P>
            Der Widerruf muss so einfach sein wie die Einwilligung. Ein Banner, das man einmal
            wegklickt und danach nie wiederfindet, erfüllt das nicht.
          </P>
          <P>
            Das Badge ist der eingebaute Weg dorthin: ein kleiner, dauerhafter Aufruf in einer
            Ecke. Wer es abschaltet, muss selbst einen Link setzen — die Alternative ist keine
            Alternative, sondern ein fehlender Widerruf.
          </P>
          <Terms
            items={[
              ['Modus „kein Badge“', 'Nur sinnvoll, wenn Ihre Seite selbst einen Link auf den Auswahl-Dialog anbietet.'],
              ['Position', 'Unten links kollidiert seltener mit Chat-Widgets, die üblicherweise unten rechts sitzen.'],
            ]}
          />
        </>
      ),
      en: (
        <>
          <P>
            Withdrawing consent has to be as easy as giving it. A banner you dismiss once and can
            never find again does not satisfy that.
          </P>
          <P>
            The badge is the built-in route back: a small, permanent handle in a corner. Switch it
            off and you have to provide your own link — the alternative is not an alternative, it
            is a missing right of withdrawal.
          </P>
          <Terms
            items={[
              ['Mode "no badge"', 'Only sensible if your own page offers a link that opens the preferences dialog.'],
              ['Position', 'Bottom left collides less often with chat widgets, which usually sit bottom right.'],
            ]}
          />
        </>
      ),
    },
  },

  'auswahl-dialog': {
    group: 'banner',
    title: { de: 'Auswahl-Dialog', en: 'Preferences dialog' },
    teaser: {
      de: 'Die granulare Auswahl, und die Kennung für Auskunftsanfragen.',
      en: 'The granular choice, and the reference for subject access requests.',
    },
    body: {
      de: (
        <>
          <P>
            Der Auswahl-Dialog ist der zweite Bildschirm: hier werden Kategorien einzeln erlaubt
            und die genannten Anbieter aufgeführt. Was Sie unter „Anbieter“ pflegen, erscheint
            hier — und nur hier werden die Empfänger nach Art. 13 tatsächlich benannt.
          </P>
          <P>
            In der Einleitung wird <C>{'{consent_id}'}</C> durch die Kennung dieser Einwilligung
            ersetzt. Damit kann ein Besucher bei einer Auskunftsanfrage genau seinen Datensatz
            benennen, und Sie finden ihn unter „Nachweise“ ohne Rückfrage.
          </P>
        </>
      ),
      en: (
        <>
          <P>
            The preferences dialog is the second screen: categories are allowed individually here
            and the named vendors are listed. Whatever you maintain under "Vendors" appears here —
            and only here are the recipients under Art. 13 actually named.
          </P>
          <P>
            In the introduction, <C>{'{consent_id}'}</C> is replaced by the reference for that
            consent. A visitor making a subject access request can then name their exact record,
            and you can find it under "Proofs" without asking them anything further.
          </P>
        </>
      ),
    },
  },

  sprachen: {
    group: 'banner',
    title: { de: 'Sprachen', en: 'Languages' },
    teaser: {
      de: 'Wie mehrsprachige Texte gepflegt werden und was bei einer Lücke passiert.',
      en: 'How multilingual texts work, and what happens when one is missing.',
    },
    body: {
      de: (
        <>
          <P>
            Die Sprachauswahl in der Werkzeugleiste schaltet um, welche Fassung Sie gerade
            bearbeiten — sie ändert nichts an der Darstellung selbst. Jede Sprache hat eigenen
            Titel, Text, Kategorie-Bezeichnungen und Button-Beschriftungen.
          </P>
          <P>
            Fehlt eine Übersetzung, sehen Besucher mit dieser Spracheinstellung den deutschen
            Text. Der Editor weist darauf hin, verhindert das Veröffentlichen aber nicht: ein
            vollständiges Banner in einer Sprache ist besser als ein leeres in zweien.
          </P>
        </>
      ),
      en: (
        <>
          <P>
            The language selector in the toolbar switches which version you are editing — it does
            not change the rendering itself. Each language carries its own title, body, category
            labels and button captions.
          </P>
          <P>
            Where a translation is missing, visitors with that language setting see the German
            text. The editor points this out but does not block publishing: a complete banner in
            one language beats an empty one in two.
          </P>
        </>
      ),
    },
  },

  entwurf: {
    group: 'banner',
    title: { de: 'Entwurf, Speichern, Konflikte', en: 'Draft, saving, conflicts' },
    teaser: {
      de: 'Was automatisch gespeichert wird — und was „an anderer Stelle geändert“ bedeutet.',
      en: 'What is saved automatically — and what "changed elsewhere" means.',
    },
    body: {
      de: (
        <>
          <P>
            Der Entwurf speichert sich selbst, kurz nachdem Sie aufhören zu tippen. Er ist nicht
            das, was Besucher sehen: gespeichert und veröffentlicht sind zwei verschiedene
            Dinge, und nur der zweite Schritt wirkt nach außen.
          </P>
          <Terms
            items={[
              ['Konflikt', 'Jemand anderes hat denselben Entwurf geändert. „Erneut speichern“ setzt Ihre Fassung durch; „Serverstand laden“ verwirft Ihre — samt Rückgängig-Verlauf.'],
              ['Rückgängig', '⌘Z und ⌘⇧Z. In einem Textfeld gehört ⌘Z dem Textfeld, damit die zeichenweise Korrektur erhalten bleibt.'],
              ['⌘S', 'Speichert sofort, statt auf die Pause zu warten. ⌘↵ öffnet den Veröffentlichen-Dialog.'],
            ]}
          />
        </>
      ),
      en: (
        <>
          <P>
            The draft saves itself shortly after you stop typing. It is not what visitors see:
            saved and published are two different things, and only the second one reaches the
            outside world.
          </P>
          <Terms
            items={[
              ['Conflict', 'Someone else changed the same draft. "Save again" forces your version through; "load server version" discards yours, undo history included.'],
              ['Undo', '⌘Z and ⌘⇧Z. Inside a text field ⌘Z belongs to the field, so per-character correction keeps working.'],
              ['⌘S', 'Saves immediately instead of waiting for the pause. ⌘↵ opens the publish dialog.'],
            ]}
          />
        </>
      ),
    },
  },

  veroeffentlichen: {
    group: 'banner',
    title: { de: 'Veröffentlichen und Zurückrollen', en: 'Publishing and rolling back' },
    teaser: {
      de: 'Was der Vergleich zeigt, welche Änderung erneut fragt, und wie der Weg zurück geht.',
      en: 'What the diff shows, which change re-prompts everyone, and how to get back.',
    },
    body: {
      de: (
        <>
          <P>
            Der Dialog vergleicht Ihren Entwurf mit dem, was Besucher gerade bekommen — nicht mit
            dem letzten Speicherstand. Bei laufendem Autosave wäre „seit dem letzten Speichern
            geändert“ jedes Mal leer.
          </P>
          <P>
            Verglichen wird das Banner: Texte, Farben, Kategorien. Regeln, Anbieter und Ausnahmen
            gehören nicht zum Entwurf — sie werden beim Veröffentlichen frisch gebaut. Ein leerer
            Vergleich heißt deshalb „am Banner ändert sich nichts“ und nicht „es gibt nichts zu
            veröffentlichen“. Wer eine Blockier-Regel geändert hat, veröffentlicht hier trotzdem.
          </P>
          <P>
            Mit <strong>●</strong> markierte Zeilen sind inhaltliche Änderungen: sie ändern den
            Wortlaut oder die Vorauswahl, damit den Nachweis-Hash, und damit wird jeder Besucher
            mit bestehender Einwilligung erneut gefragt. Eine Farbänderung tut das nicht.
          </P>
          <P>
            Der Verlauf listet jede veröffentlichte Fassung mit Zeitpunkt und Urheber.
            <strong> Zurückrollen</strong> schaltet eine davon wieder live; Ihr Entwurf bleibt
            dabei unverändert, der Editor bietet also weiterhin an, ihn zu veröffentlichen.
          </P>
          <Note>
            Veröffentlichen dürfen Administrator und Inhaber. Die Rolle „Redakteur“ darf
            bearbeiten und nicht veröffentlichen — das ist der Zweck der Rolle, kein fehlendes
            Recht.
          </Note>
        </>
      ),
      en: (
        <>
          <P>
            The dialog compares your draft with what visitors are getting right now — not with the
            last save. With autosave running, "changed since the last save" would be empty every
            time you asked.
          </P>
          <P>
            What it compares is the banner: texts, colours, categories. Rules, vendors and their
            exceptions are not part of the draft — they are built fresh on publishing. So an empty
            comparison means "nothing changes about the banner", not "there is nothing to publish".
            If you changed a blocking rule, you still publish here.
          </P>
          <P>
            Rows marked <strong>●</strong> are material changes: they alter the wording or the
            pre-selection, therefore the proof hash, and therefore every visitor with an existing
            consent is asked again. A colour change does not.
          </P>
          <P>
            The history lists every published version with its time and author.
            <strong> Roll back</strong> puts one of them live again; your draft is left untouched,
            so the editor will still offer to publish it.
          </P>
          <Note>
            Admins and owners may publish. The "editor" role may edit and not publish — that is
            the purpose of the role, not a missing permission.
          </Note>
        </>
      ),
    },
  },
} as const satisfies Record<string, Topic>;
